<template>
  <div>
    <BeatLoader
      v-if="loading || !user.auth0id || !disclaimerConfig"
      class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
      color="var(--color-primary)"
      size="25px" />

    <div
      v-else
      class="max-w-[560px] w-full text-left py-[40px] px-[30px] md:p-[40px] bg-white rounded-lg border-2 border-blue-5 otivo-drop-shadow">

      <div class="headline-5 bold mb-6">
        Please confirm before proceeding...
      </div>

      <!-- Consent Section -->
      <SuperfundConsentSection
        v-if="processedConsent"
        :consent="processedConsent"
        :acknowledged="disclaimerState?.consentAcknowledged || false"
        @toggle="toggleConsentCheckbox" />

      <!-- Terms Content -->
      <SuperfundTermsContent
        v-if="processedTermsParagraphs?.length > 0"
        :paragraphs="processedTermsParagraphs" />

      <!-- Privacy Statement -->
      <SuperfundPrivacyStatement
        v-if="processedPrivacyStatement"
        :statement="processedPrivacyStatement"
        :open="disclaimerState?.privacyStatementOpened || false"
        @toggle="togglePrivacyStatement" />


      <!-- Action Buttons -->
      <div class="flex flex-col w-full gap-[20px] md:w-fit pt-10">
        <OtivoButton
          size="large"
          :disabled="!canProceed"
          @click="handleProceed"
          colour="blue"
          class="w-full"
          data-test="proceedButton"
          :loading="submitLoading"
          :large-text="true">
          Proceed
        </OtivoButton>

        <OtivoButton
          size="large"
          colour="white"
          class="w-full md:w-fit"
          @click="handleReject">
          Do not proceed
        </OtivoButton>
      </div>
    </div>

<!--    <div v-else-if="error" class="text-center p-8">-->
<!--      <p class="text-red-600 mb-4">{{ error }}</p>-->
<!--      <OtivoButton @click="loadDisclaimerConfig" colour="blue">-->
<!--        Retry-->
<!--      </OtivoButton>-->
<!--    </div>-->
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import BeatLoader from 'vue-spinner/src/BeatLoader.vue'
import OtivoButton from '@/components/OtivoButton.vue'
import SuperfundPrivacyStatement from './SuperfundPrivacyStatement.vue'
import SuperfundConsentSection from './SuperfundConsentSection.vue'
import SuperfundTermsContent from './SuperfundTermsContent.vue'

import { useUserStore } from '@/store/pinia/UserStore'
import { useAccountCreationStore } from '@/store/pinia/AccountCreationStore'
import { useModalStore } from '@/store/pinia/ModalStore'
import { useSuperfundDisclaimer } from '@/composables/useSuperfundDisclaimer'
import { sendLegalWelcomeEmail } from '@/store/pinia/adminPortal/EmailManager'
import UserRenewedWelcomeBackModal from '@/components/Modals/UserRenewedWelcomeBackModal.vue'
import User from '@/types/api/account/User'

const router = useRouter()
const userStore = useUserStore()
const accountCreationStore = useAccountCreationStore()
const modalStore = useModalStore()

const {
  disclaimerConfig,
  disclaimerState,
  loading,
  error,
  processedConsent,
  processedTermsParagraphs,
  processedPrivacyStatement,
  canProceed,
  loadDisclaimerConfig,
  acknowledgePriorDisclaimer,
  togglePrivacyStatement,
  toggleConsentCheckbox
} = useSuperfundDisclaimer()


const submitLoading = ref(false)

const user = computed((): User => userStore.getUser)
const isNewUser = computed(() => !user.value.fee_consent_expires_at)

const emit = defineEmits<{
  (e: 'next', data: string): void
}>()

onMounted(() => {
  if (!user.value || Object.keys(user.value).length === 0) {
    router.push('/dashboard')
    return
  }
  // Config is loaded synchronously in the composable
})

const handleProceed = async () => {
  if (!canProceed.value) return

  submitLoading.value = true

  try {
    const wasNewUser = isNewUser.value
    const { auth0id, account_id, fee_consent_expires_at, account_deactivated } = user.value

    if (wasNewUser) {
      await sendLegalWelcomeEmail(auth0id)
      await accountCreationStore.setTermsAndConditionsAgreed(account_id)
    } else {
      await accountCreationStore.renewTermsAndConditionsAgreed(
        account_id,
        fee_consent_expires_at,
        account_deactivated,
      )
    }

    await userStore.fetchUser()
    emit('next', 'terms and conditions accepted')

    if (!wasNewUser) {
      modalStore.openModal(UserRenewedWelcomeBackModal, {
        handleCloseModal: modalStore.closeModal,
      })
    }
  } catch (error) {
    console.error('Error processing disclaimer acceptance:', error)
  } finally {
    submitLoading.value = false
  }
}

const handleReject = () => {
  router.push('/dashboard')
}
</script>
